import {
  type ComputedRef,
  computed,
  nextTick,
  type ShallowRef,
  shallowRef,
} from 'vue';
import { store } from '@/app/store';
import { animations } from '@/services/Animations';
import { faceDetection } from '@/services/FaceDetection';

const DISPLAYED_PHOTOS_LIMIT = 4;

export interface UploadedSidebarPhoto {
  animateFromRef?: HTMLElement | object;
  url?: string | null;
  uuid: string;
}

export interface UploadedPhotoItemHandle {
  animateFrom: (fromRef?: HTMLElement | object) => Promise<void>;
  readonly photoUuid: string;
}

interface UseUploadedPhotosSidebarOptions {
  getContainer: () => HTMLElement | null;
  getExtraPhotoCounter: () => HTMLElement | null;
  getPhotoItems: () => readonly UploadedPhotoItemHandle[];
  onAnimationComplete: () => void;
  watchUploads: () => boolean;
}

export interface UploadedPhotosSidebarController {
  addToQueue: (photos: UploadedSidebarPhoto[]) => void;
  animatedPhotoUuids: ShallowRef<string[]>;
  containerOffsetTop: ShallowRef<number>;
  displayedPhotos: ComputedRef<UploadedSidebarPhoto[]>;
  enter: () => void;
  extraPhotoCount: ComputedRef<number>;
  hasPhotoAnimated: (photo: UploadedSidebarPhoto) => boolean;
  haveAnyPhotosAnimated: ComputedRef<boolean>;
  isAnimating: ShallowRef<boolean>;
  isContainerOffsetTopTransitionEnabled: ShallowRef<boolean>;
  isExtraPhotoCounterVisible: ComputedRef<boolean>;
  isPhotoAnimating: (photo: UploadedSidebarPhoto) => boolean;
  lazyAnimateQueue: (isContinuing?: boolean) => Promise<void>;
  leave: () => void;
  onPhotoUpload: (photos: UploadedSidebarPhoto[]) => void;
  queue: ShallowRef<UploadedSidebarPhoto[]>;
  updateContainerOffsetTop: () => Promise<void>;
}

export function useUploadedPhotosSidebar({
  getContainer,
  getExtraPhotoCounter,
  getPhotoItems,
  onAnimationComplete,
  watchUploads,
}: UseUploadedPhotosSidebarOptions): UploadedPhotosSidebarController {
  const containerOffsetTop = shallowRef(0);
  const isContainerOffsetTopTransitionEnabled = shallowRef(false);
  const isAnimating = shallowRef(false);
  const queue = shallowRef<UploadedSidebarPhoto[]>([]);
  const animatedPhotoUuids = shallowRef<string[]>([]);
  let disposed = false;
  let entered = false;
  let pendingRafId: number | null = null;

  const uploadedPhotos = computed(
    () => store.photosUploaded as UploadedSidebarPhoto[]
  );
  const displayedPhotos = computed(() =>
    uploadedPhotos.value.slice(0, DISPLAYED_PHOTOS_LIMIT)
  );
  const haveAnyPhotosAnimated = computed(
    () => animatedPhotoUuids.value.length > 0
  );
  const extraPhotoCount = computed(
    () => uploadedPhotos.value.length - displayedPhotos.value.length
  );
  const isExtraPhotoCounterVisible = computed(
    () => Boolean(extraPhotoCount.value) && !isAnimating.value
  );

  function cleanFromQueue(photo: UploadedSidebarPhoto): void {
    queue.value = queue.value.filter(({ uuid }) => uuid !== photo.uuid);
  }

  function isPhotoAnimating(photo: UploadedSidebarPhoto): boolean {
    return queue.value[0]?.uuid === photo.uuid;
  }

  function hasPhotoAnimated(photo: UploadedSidebarPhoto): boolean {
    return animatedPhotoUuids.value.includes(photo.uuid);
  }

  function findPhotoItem(uuid: string): UploadedPhotoItemHandle | undefined {
    return getPhotoItems().find((item) => item.photoUuid === uuid);
  }

  function lazyAnimateQueue(isContinuing = false): Promise<void> {
    return new Promise((resolve) => {
      if (disposed) {
        resolve();
        return;
      }

      void nextTick(() => {
        if (disposed) {
          resolve();
          return;
        }

        if (!isContinuing && isAnimating.value) {
          resolve();
          return;
        }

        const currentPhoto = queue.value[0];
        if (!currentPhoto) {
          isAnimating.value = false;
          resolve();
          return;
        }

        isAnimating.value = true;
        const photoItem = findPhotoItem(currentPhoto.uuid);
        if (!photoItem) {
          cleanFromQueue(currentPhoto);
          resolve(lazyAnimateQueue(true));
          return;
        }

        photoItem.animateFrom(currentPhoto.animateFromRef).then(() => {
          if (disposed) {
            resolve();
            return;
          }
          cleanFromQueue(currentPhoto);
          animatedPhotoUuids.value = [
            ...animatedPhotoUuids.value,
            currentPhoto.uuid,
          ];
          resolve(lazyAnimateQueue(true));
        });
      });
    });
  }

  function addToQueue(photos: UploadedSidebarPhoto[]): void {
    const displayedOrPendingPhotoCount = Math.max(
      animatedPhotoUuids.value.length,
      queue.value.length
    );

    if (displayedOrPendingPhotoCount < DISPLAYED_PHOTOS_LIMIT) {
      const photosToAnimateCount =
        DISPLAYED_PHOTOS_LIMIT - displayedOrPendingPhotoCount;
      queue.value = [...queue.value, ...photos.slice(0, photosToAnimateCount)];
    }

    void lazyAnimateQueue().then(() => {
      if (disposed) {
        return;
      }
      const counter = getExtraPhotoCounter();
      if (extraPhotoCount.value && !isAnimating.value && counter) {
        animations.pulseText(counter, {
          duration: 800,
          scale: 2.2,
        });
      }
      onAnimationComplete();
    });
  }

  function updateContainerOffsetTop(): Promise<void> {
    return new Promise((resolve) => {
      if (disposed) {
        resolve();
        return;
      }

      void nextTick(() => {
        if (disposed) {
          resolve();
          return;
        }
        const container = getContainer();
        if (!container) {
          resolve();
          return;
        }
        containerOffsetTop.value =
          (document.body.offsetHeight -
            container.getBoundingClientRect().height) /
          2;
        pendingRafId = requestAnimationFrame(() => {
          pendingRafId = null;
          resolve();
        });
      });
    });
  }

  function onPhotoUpload(photos: UploadedSidebarPhoto[]): void {
    if (disposed) {
      return;
    }
    addToQueue(photos);
    void updateContainerOffsetTop();
  }

  function enter(): void {
    if (entered) {
      return;
    }
    entered = true;
    disposed = false;
    animatedPhotoUuids.value = uploadedPhotos.value.map(({ uuid }) => uuid);
    void updateContainerOffsetTop().then(() => {
      if (!disposed) {
        isContainerOffsetTopTransitionEnabled.value = true;
      }
    });
    if (watchUploads()) {
      faceDetection.on(faceDetection.EVENTS.NEW_PHOTOS_UPLOADED, onPhotoUpload);
    }
  }

  function leave(): void {
    if (disposed) {
      return;
    }
    disposed = true;
    isContainerOffsetTopTransitionEnabled.value = false;
    isAnimating.value = false;
    queue.value = [];
    if (pendingRafId !== null) {
      cancelAnimationFrame(pendingRafId);
      pendingRafId = null;
    }
    faceDetection.off(faceDetection.EVENTS.NEW_PHOTOS_UPLOADED, onPhotoUpload);
  }

  return {
    addToQueue,
    animatedPhotoUuids,
    containerOffsetTop,
    displayedPhotos,
    enter,
    extraPhotoCount,
    hasPhotoAnimated,
    haveAnyPhotosAnimated,
    isAnimating,
    isContainerOffsetTopTransitionEnabled,
    isExtraPhotoCounterVisible,
    isPhotoAnimating,
    lazyAnimateQueue,
    leave,
    onPhotoUpload,
    queue,
    updateContainerOffsetTop,
  };
}
